/**
 * Women.jsx — EVORA Women's Collection Page
 * Refactored: SEO, Performance, Accessibility, Conversion
 *
 * KEY FIXES vs v3:
 * - useCallback deps arrays were missing BASE_URL (stale closure bug)
 * - fetchSubcategories defined before state it uses → moved after
 * - useEffect for fetchSubcategories passed wrong deps (location.pathname only)
 * - Product fetch fired even on isMainPage if deps changed
 * - Missing aria-label on <picture> hero image
 * - Hero LCP image had no explicit width/height (CLS)
 * - Recently Viewed fetched on every render (no deps guard)
 * - BASE_URL defined in module scope but used inside useCallback (no dep)
 * - buildPageNumbers returned wrong ellipsis keys on re-render
 * - Subcategory breadcrumb label only handled 2 slugs (hardcoded)
 * - FilterDrawer rendered on isMainPage unmount was causing state leak
 * - No Suspense/lazy for FilterDrawer (added)
 * - Twitter card meta was missing entirely
 * - robots meta missing
 * - Schema `@type: Offer` missing from CollectionPage schema
 * - recentlyViewed fetch had no guard for empty ids array → unnecessary request
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  lazy,
  Suspense,
  useRef,
} from 'react';
import { Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/common/Layout/Layout';
import ProductCard from '../../components/product/ProductCard/ProductCard';
import { useToast } from '../../context/ToastContext';
import { getRecentlyViewed, saveRecentlyViewed } from '../../utils/recentlyViewed';
import {
  IconShield,
  IconTruck,
  IconReturn,
  IconLock,
  IconStar,
  IconFilter,
  IconArrowRight,
  IconChevronRight,
} from '../../components/common/Icons/Icons';
import { formatCurrency } from '../../utils/currency';
import { getImageUrl } from '../../utils/imageUtils';
import ApiImage, { ensureAbsoluteSrc } from '../../components/common/ApiImage';
import '../../pages/shared/recentlyViewed.css';
import '../../pages/shared/pagination.css';
import './Women.css';

// ── Lazy-load heavy drawer (not needed on first render) ──────
const FilterDrawer = lazy(() =>
  import('../../components/filters/FilterDrawer/FilterDrawer')
);

// ─── Constants — single source of truth ────────────────────────
const BRAND_NAME    = 'EVORA';
const BRAND_URL     = 'https://evoraclothing.com';
// Free shipping on all orders (no threshold). Returns: size & defective only, 7 days (see /returns).
const RETURNS_DAYS  = '7';
const PRODUCTS_PER_PAGE = 20;

// ── ENV guard — fail fast in prod if misconfigured ───────────
const BASE_URL = (() => {
  const url = process.env.REACT_APP_API_URL;
  if (!url && process.env.NODE_ENV === 'production') {
    throw new Error('[EVORA] REACT_APP_API_URL is not set.');
  }
  return url || 'http://localhost:5000/api';
})();

const SORT_OPTIONS = [
  { value: 'popular',    label: 'Popular' },
  { value: 'newest',     label: 'Newest' },
  { value: 'price-low',  label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
];

// Per-route OG images (faceted/shared URL best practice — Semrush/Google)
const OG_IMAGES = {
  'round-neck': `${BRAND_URL}/og/women-roundneck.jpg`,
  polo:         `${BRAND_URL}/og/women-polo.jpg`,
  default:      `${BRAND_URL}/og/women-collection.jpg`,
};

// ── Analytics — silent in production ────────────────────────
const trackEvent = (name, data = {}) => {
  if (typeof window !== 'undefined' && window.gtag) window.gtag('event', name, data);
  if (process.env.NODE_ENV === 'development') console.log('[Analytics]', name, data);
};

// ── Pagination helper ────────────────────────────────────────
function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([
    1, total,
    current - 2, current - 1, current, current + 1, current + 2,
  ]);
  const valid = [...pages].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < valid.length; i++) {
    if (i > 0 && valid[i] - valid[i - 1] > 1) result.push('...');
    result.push(valid[i]);
  }
  return result;
}

// ── Subcategory display name helper (not hardcoded to 2 slugs) ──
const slugToDisplayName = (slug, subcategories) => {
  const found = subcategories.find(s => s.slug === slug);
  if (found) return found.name;
  // Fallback: prettify slug
  return slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// ── Small icon wrappers ──────────────────────────────────────
const IconFilters = () => <IconFilter size={16} aria-hidden="true" />;
const IconArrow   = () => <IconArrowRight size={13} aria-hidden="true" />;

// ════════════════════════════════════════════════════════════
//  COMPONENT
// ════════════════════════════════════════════════════════════
function Women() {
  const location       = useLocation();
  const navigate       = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Path parsing (supports /women and /shop/women) ───────────────────────
  const { basePath, categorySlug, pathSubCategory } = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const isShopWomen = parts[0] === 'shop' && parts[1] === 'women';
    if (isShopWomen) {
      return {
        basePath: '/shop/women',
        categorySlug: 'women',
        pathSubCategory: parts.length >= 3 ? parts[2] : null,
      };
    }
    return {
      basePath: '/women',
      categorySlug: parts[0] || 'women',
      pathSubCategory: parts.length >= 2 ? parts[1] : null,
    };
  }, [location.pathname]);

  const isMainPage = !pathSubCategory;

  const sort          = searchParams.get('sort')          || 'popular';
  const page          = parseInt(searchParams.get('page'), 10) || 1;
  const sizeParam     = searchParams.get('size')           || '';
  const priceMinParam = searchParams.get('priceMin')       || '';
  const priceMaxParam = searchParams.get('priceMax')       || '';
  const isNewParam   = searchParams.get('isNew') === 'true';

  const filters = useMemo(() => ({
    size:         sizeParam ? [sizeParam] : [],
    priceMin:     priceMinParam,
    priceMax:     priceMaxParam,
    subCategory:  pathSubCategory ? [pathSubCategory] : [],
    isNew:        isNewParam,
  }), [sizeParam, priceMinParam, priceMaxParam, pathSubCategory, isNewParam]);

  const activeFilterCount =
    (filters.size?.length  || 0) +
    ((priceMinParam || priceMaxParam) ? 1 : 0) +
    (isNewParam ? 1 : 0);

  const preferredBasePath = categorySlug === 'women' ? '/women' : basePath;

  // ── State ───────────────────────────────────────────────
  const [subcategories,   setSubcategories]   = useState([]);
  const [products,        setProducts]        = useState([]);
  const [loading,         setLoading]         = useState(!isMainPage);
  const [totalPages,      setTotalPages]      = useState(1);
  const [totalProducts,   setTotalProducts]   = useState(0);
  const [hasNextPage,     setHasNextPage]     = useState(false);
  const [hasPrevPage,     setHasPrevPage]     = useState(false);
  const [isFilterOpen,    setIsFilterOpen]    = useState(false);
  const [recentlyViewed,  setRecentlyViewed]  = useState([]);
  const [category,         setCategory]         = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [categoryError,   setCategoryError]   = useState(null);
  const [subcategoryError, setSubcategoryError] = useState(null);
  const [productError,    setProductError]    = useState(null);
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);

  // Hero image URL for preload / reset (must be defined before useEffect that uses it)
  const heroFallbackSrc =
    pathSubCategory === 'round-neck' ? '/women-roundneck-hero.jpg'
      : pathSubCategory === 'polo' ? '/women-polo-hero.jpg'
      : '/women-hero.jpg';
  // Subcategory pages: use uploaded subcategory image when available, else static. Main /women: wait for category fetch.
  const matchingSub = pathSubCategory && subcategories.find(s => s.slug === pathSubCategory);
  const heroSrc =
    pathSubCategory && (pathSubCategory === 'round-neck' || pathSubCategory === 'polo')
      ? (matchingSub?.image ? getImageUrl(matchingSub.image, matchingSub.updatedAt, heroFallbackSrc) : heroFallbackSrc)
      : categoryLoading
        ? null
        : (category?.image
            ? getImageUrl(category.image, category.updatedAt, '/hero.png')
            : heroFallbackSrc);

  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  useEffect(() => { setHeroImageLoaded(false); }, [heroSrc]);

  // ══════════════════════════════════════════════════════════
  //  FETCH: Subcategories (aligned with Men.jsx pattern)
  // ══════════════════════════════════════════════════════════
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function run(attempt = 0) {
      setCategoryLoading(true);
      setCategoryError(null);
      setSubcategoryError(null);

      const timeout = setTimeout(() => controller.abort('timeout'), 30_000);

      try {
        // Step 1 — fetch category by slug
        const catRes = await fetch(`${BASE_URL}/categories/slug/${categorySlug}`, { signal, cache: 'no-store' });
        clearTimeout(timeout);
        if (!catRes.ok) throw Object.assign(new Error(`HTTP ${catRes.status}`), { status: catRes.status });

        const catData = await catRes.json();
        if (signal.aborted) return;
        if (!catData.success || !catData.category) {
          setCategoryError('Category not found.');
          return;
        }

        // Store category data (includes image)
        setCategory(catData.category);

        // Step 2 — fetch subcategories using category ID
        const subTimeout = setTimeout(() => controller.abort('timeout'), 30_000);
        const subRes = await fetch(
          `${BASE_URL}/subcategories?categoryId=${catData.category._id}`,
          { signal, cache: 'no-store' },
        );
        clearTimeout(subTimeout);
        if (!subRes.ok) throw Object.assign(new Error(`HTTP ${subRes.status}`), { isSub: true });

        const subData = await subRes.json();
        if (signal.aborted) return;
        if (subData.success) {
          const subs = subData.subcategories ?? [];
          setSubcategories(subs);
          if (process.env.NODE_ENV === 'development') {
            console.log('[Women] Subcategories loaded:', subs.length);
          }
        }

      } catch (err) {
        clearTimeout(timeout);
        if (signal.aborted) return; // unmounted or timed out — silent

        const MAX = 2;
        if (attempt < MAX) {
          const delay = Math.min(1_000 * 2 ** attempt, 5_000);
          setTimeout(() => { if (!signal.aborted) run(attempt + 1); }, delay);
          return;
        }
        // All retries exhausted
        if (err.isSub) {
          setSubcategoryError('Failed to load subcategories. Please refresh.');
        } else {
          setCategoryError('Failed to load category. Please refresh.');
        }
        console.error('[Women] Category fetch error:', err);
      } finally {
        if (!signal.aborted) setCategoryLoading(false);
      }
    }

    run();
    return () => controller.abort();
  }, [location.pathname, categorySlug]);

  // ══════════════════════════════════════════════════════════
  //  FETCH: Products (subcategory pages only) - aligned with Men.jsx
  // ══════════════════════════════════════════════════════════
  useEffect(() => {
    if (isMainPage) return;

    const controller = new AbortController();
    const { signal } = controller;

    async function run(attempt = 0) {
      setLoading(true);
      setProductError(null);

      const timeout = setTimeout(() => controller.abort('timeout'), 30_000);

      try {
        const params = new URLSearchParams({
          limit:  String(PRODUCTS_PER_PAGE),
          sort,
          page:   String(page),
          status: 'active',
          categorySlug,
        });
        if (pathSubCategory) params.append('subcategorySlug', pathSubCategory);
        if (sizeParam)       params.append('size',         sizeParam);
        if (priceMinParam)   params.append('priceMin',     priceMinParam);
        if (priceMaxParam)   params.append('priceMax',     priceMaxParam);
        if (isNewParam)      params.append('isNew',        'true');

        const res = await fetch(`${BASE_URL}/products?${params}`, { signal, cache: 'no-store' });
        clearTimeout(timeout);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        if (signal.aborted) return;

        if (data.success) {
          setProducts(data.products ?? []);
          setTotalPages(data.totalPages ?? 1);
          setTotalProducts(data.totalProducts ?? data.total ?? 0);
          setHasNextPage(data.pagination?.hasNextPage ?? page < (data.totalPages ?? 1));
          setHasPrevPage(data.pagination?.hasPrevPage ?? page > 1);
        }
      } catch (err) {
        clearTimeout(timeout);
        if (signal.aborted) return;

        const MAX = 3;
        if (attempt < MAX) {
          const delay = Math.min(1_000 * 2 ** attempt, 10_000);
          setTimeout(() => { if (!signal.aborted) run(attempt + 1); }, delay);
          return;
        }
        setProductError('Failed to load products. Please try again.');
        showToastRef.current("We couldn't load these products. Check your connection and try again.", 'error');
        console.error('[Women] Product fetch error:', err);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    }

    run();
    return () => controller.abort();
  }, [
    isMainPage, pathSubCategory, categorySlug,
    sort, page, sizeParam, priceMinParam, priceMaxParam, isNewParam,
  ]);

  // ══════════════════════════════════════════════════════════
  //  FETCH: Recently Viewed (robust to legacy object entries; dedupe IDs)
  // ══════════════════════════════════════════════════════════
  const rvEntries = getRecentlyViewed().slice(0, 5);
  const rvIds = rvEntries
    .map((entry) => {
      if (typeof entry === 'object' && entry !== null) {
        return (entry._id || entry.id || '').toString().trim();
      }
      return typeof entry === 'string' ? entry.trim() : String(entry).trim();
    })
    .filter(Boolean);
  const rvIdsUnique = rvIds.filter((id, i) => rvIds.indexOf(id) === i);
  const rvIdString  = rvIdsUnique.join(',');

  useEffect(() => {
    if (!rvIdString) return;

    const controller = new AbortController();
    const timeout    = setTimeout(() => controller.abort('timeout'), 30_000);
    const idsOrder   = rvIdString.split(',').filter(Boolean);

    fetch(`${BASE_URL}/products?ids=${rvIdString}`, { signal: controller.signal, cache: 'no-store' })
      .then((r) => { clearTimeout(timeout); if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => {
        if (controller.signal.aborted || !d.success) return;
        const products = d.products ?? [];
        const ordered = idsOrder
          .map((id) => products.find((p) => (p._id || p.id)?.toString() === id))
          .filter(Boolean);
        setRecentlyViewed(ordered);
      })
      .catch(() => { clearTimeout(timeout); /* silent — recently viewed is non-critical */ });

    return () => { clearTimeout(timeout); controller.abort(); };
  }, [rvIdString]);

  // ── Analytics ───────────────────────────────────────────
  useEffect(() => {
    const title = pathSubCategory
      ? `Women's ${slugToDisplayName(pathSubCategory, subcategories)} | ${BRAND_NAME}`
      : `Shop Women | ${BRAND_NAME}`;
    trackEvent('page_view', { page_title: title, page_location: window.location.href });
  }, [pathSubCategory, subcategories]);

  // ══════════════════════════════════════════════════════════
  //  HANDLERS
  // ══════════════════════════════════════════════════════════
  const handleFiltersChange = useCallback((newFilters) => {
    const selectedSub = newFilters.subCategory?.[0] ?? newFilters.subcategory?.[0] ?? null;
    const params      = new URLSearchParams();
    if (newFilters.size?.[0])   params.set('size',     newFilters.size[0]);
    if (newFilters.priceMin)   params.set('priceMin', newFilters.priceMin);
    if (newFilters.priceMax)   params.set('priceMax', newFilters.priceMax);
    if (newFilters.isNew)      params.set('isNew',     'true');
    if (sort !== 'popular')    params.set('sort',     sort);

    const subExists = subcategories.some(s => s.slug === selectedSub);
    const path      = selectedSub && subExists ? `${preferredBasePath}/${selectedSub}` : preferredBasePath;
    const qs        = params.toString();
    navigate(`${path}${qs ? `?${qs}` : ''}`, { replace: false });
  }, [navigate, sort, subcategories, preferredBasePath]);

  const handleSortChange = useCallback((e) => {
    const p = new URLSearchParams(searchParams);
    p.set('sort', e.target.value);
    p.delete('page');
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  const handlePageChange = useCallback((newPage) => {
    const p = new URLSearchParams(searchParams);
    p.set('page', String(newPage));
    setSearchParams(p, { replace: true });
    // Scroll to products section after state update
    requestAnimationFrame(() => {
      document.getElementById('women-products-section')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [searchParams, setSearchParams]);

  const handleAddToCart = useCallback((productName, productId) => {
    showToastRef.current(`${productName} added to your bag — check it out when you're ready.`, 'success');
    const product = products.find(p => (p._id || p.id) === productId);
    trackEvent('add_to_cart', {
      currency: 'INR',
      value:    product?.price ?? 0,
      items:    [{ item_id: productId, item_name: productName }],
    });
  }, [products]);

  const handleProductClick = useCallback((product) => {
    const id = product._id || product.id;
    saveRecentlyViewed(id);
    trackEvent('select_item', {
      item_list_id:   'shop_women',
      item_list_name: 'Shop Women',
      items: [{ item_id: id, item_name: product.name, price: product.price }],
    });
  }, []);

  const clearFilters = useCallback(() => {
    handleFiltersChange({
      size: [], priceMin: '', priceMax: '',
      subCategory: pathSubCategory ? [pathSubCategory] : [],
      isNew: false,
    });
  }, [handleFiltersChange, pathSubCategory]);

  const handleClearRecentlyViewed = useCallback(() => {
    localStorage.removeItem('recentlyViewed');
    setRecentlyViewed([]);
  }, []);

  // ══════════════════════════════════════════════════════════
  //  SEO META
  // ══════════════════════════════════════════════════════════
  const subcategoryMeta = useMemo(() => {
    const result = {};
    subcategories.forEach(sub => {
      result[sub.slug] = {
        title:    `Women's ${sub.name} T-Shirts | Premium Cotton | ${BRAND_NAME}`,
        description: `Shop premium ${sub.name.toLowerCase()} t-shirts for women at ${BRAND_NAME}. High-quality cotton, refined fit. Free shipping on all orders.`,
        h1:       `Women's ${sub.name} T-Shirts`,
        subtitle: sub.seoSubtitle || `Premium ${sub.name.toLowerCase()} tees crafted for comfort and refined everyday wear.`,
      };
    });
    return result;
  }, [subcategories]);

  const currentMeta = useMemo(() => {
    if (pathSubCategory && subcategoryMeta[pathSubCategory]) {
      return { ...subcategoryMeta[pathSubCategory], ogImage: OG_IMAGES[pathSubCategory] || OG_IMAGES.default };
    }
    if (pathSubCategory) {
      const name = slugToDisplayName(pathSubCategory, subcategories);
      return {
        title:       `Women's ${name} | ${BRAND_NAME}`,
        description: `Shop women's ${name.toLowerCase()} at ${BRAND_NAME}. Free shipping on all orders. Returns for size & defective only (${RETURNS_DAYS} days).`,
        h1:          `Women's ${name}`,
        subtitle:    `Premium quality. Thoughtfully designed.`,
        ogImage:     OG_IMAGES[pathSubCategory] || OG_IMAGES.default,
      };
    }
    return {
      title:       `Shop Women's T-Shirts | Premium Round Neck & Polo | ${BRAND_NAME}`,
      description: `Discover ${BRAND_NAME} women's premium t-shirts — round neck and polo styles. Free shipping across India. Returns for size & defective only (${RETURNS_DAYS} days).`,
      h1:          'Shop Women',
      subtitle:    "Timeless pieces designed for refined everyday wear.",
      ogImage:     OG_IMAGES.default,
    };
  }, [pathSubCategory, subcategoryMeta, subcategories]);

  const canonicalUrl = `${BRAND_URL}${preferredBasePath}${pathSubCategory ? `/${pathSubCategory}` : ''}`;

  const hasFacetParams =
    !!sizeParam ||
    !!priceMinParam ||
    !!priceMaxParam ||
    !!isNewParam ||
    (sort && sort !== 'popular');
  const canonicalHref = hasFacetParams
    ? canonicalUrl
    : `${canonicalUrl}${page > 1 ? `?page=${page}` : ''}`;
  const robotsContent = hasFacetParams
    ? 'noindex, follow'
    : 'index, follow, max-snippet:-1, max-image-preview:large';
  const pageUrlForOg = canonicalHref;

  const listBasePath = pathSubCategory
    ? `${preferredBasePath}/${pathSubCategory}`
    : preferredBasePath;
  const buildListUrl = useCallback((targetPage) => {
    const p = new URLSearchParams(searchParams);
    if (targetPage && targetPage > 1) p.set('page', String(targetPage));
    else p.delete('page');
    const qs = p.toString();
    return `${listBasePath}${qs ? `?${qs}` : ''}`;
  }, [listBasePath, searchParams]);

  // ── Structured Data ─────────────────────────────────────
  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type':    'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',  item: `${BRAND_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Women', item: `${BRAND_URL}${preferredBasePath}` },
      ...(pathSubCategory ? [{
        '@type':    'ListItem',
        position:   3,
        name:       currentMeta.h1,
        item:       canonicalUrl,
      }] : []),
    ],
  }), [pathSubCategory, currentMeta.h1, canonicalUrl, preferredBasePath]);

  const collectionSchema = useMemo(() => ({
    '@context':  'https://schema.org',
    '@type':     'CollectionPage',
    name:        currentMeta.title,
    description: currentMeta.description,
    url:         canonicalUrl,
    provider: {
      '@type': 'Organization',
      name:    BRAND_NAME,
      url:     BRAND_URL,
    },
    ...(!loading && products.length > 0 && {
      mainEntity: {
        '@type':        'ItemList',
        numberOfItems:  totalProducts,
        itemListElement: products.slice(0, 20).map((p, i) => {
          const stock = p.stock ?? p.variants?.reduce((s, v) => s + (v.stock ?? 0), 0);
          return {
            '@type':    'ListItem',
            position:   (page - 1) * PRODUCTS_PER_PAGE + i + 1,
            url:        `${BRAND_URL}/product/${p.slug || p._id}`,
            name:       p.name,
            ...(p.price != null && {
              offers: {
                '@type':         'Offer',
                price:           p.price,
                priceCurrency:   'INR',
                ...(typeof stock === 'number' && {
                  availability: stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                }),
              },
            }),
          };
        }),
      },
    }),
  }), [currentMeta, canonicalUrl, products, totalProducts, page, loading]);

  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════
  return (
    <Layout>
      {/* ─── SEO HEAD ─────────────────────────────────── */}
      <Helmet>
        <title>{currentMeta.title}{page > 1 ? ` — Page ${page}` : ''}</title>
        <meta name="description"        content={currentMeta.description} />
        <meta name="robots"             content={robotsContent} />

        <link rel="canonical" href={canonicalHref} />
        {!hasFacetParams && hasPrevPage && (
          <link rel="prev" href={`${canonicalUrl}${page - 1 > 1 ? `?page=${page - 1}` : ''}`} />
        )}
        {!hasFacetParams && hasNextPage && (
          <link rel="next" href={`${canonicalUrl}?page=${page + 1}`} />
        )}

        {heroSrc && (
          <link rel="preload" as="image" href={ensureAbsoluteSrc(heroSrc)} fetchPriority="high" />
        )}

        <meta property="og:type"        content="website" />
        <meta property="og:url"         content={pageUrlForOg} />
        <meta property="og:site_name"  content={BRAND_NAME} />
        <meta property="og:title"       content={currentMeta.title} />
        <meta property="og:description" content={currentMeta.description} />
        <meta property="og:image"       content={currentMeta.ogImage} />
        <meta property="og:image:width"  content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt"   content={currentMeta.h1} />
        <meta property="og:locale"      content="en_IN" />

        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={currentMeta.title} />
        <meta name="twitter:description" content={currentMeta.description} />
        <meta name="twitter:image"       content={currentMeta.ogImage} />
        <meta name="twitter:image:alt"   content={currentMeta.h1} />

        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      </Helmet>

      <div className="women-page">

        {/* ─── BREADCRUMB (semantic list for crawlability & schema alignment) ── */}
        <nav className="women-breadcrumb" aria-label="Breadcrumb">
          <ol className="women-breadcrumb__list">
            <li>
              <Link to="/" className="women-breadcrumb__link">Home</Link>
            </li>
            <li className="women-breadcrumb__separator" aria-hidden="true">
              <IconChevronRight size={12} aria-hidden="true" />
            </li>
            {pathSubCategory ? (
              <>
                <li>
                  <Link to={preferredBasePath} className="women-breadcrumb__link">Women</Link>
                </li>
                <li className="women-breadcrumb__separator" aria-hidden="true">
                  <IconChevronRight size={12} />
                </li>
                <li>
                  <span className="women-breadcrumb__current" aria-current="page">
                    {slugToDisplayName(pathSubCategory, subcategories)}
                  </span>
                </li>
              </>
            ) : (
              <li>
                <span className="women-breadcrumb__current" aria-current="page">Women</span>
              </li>
            )}
          </ol>
        </nav>

        {/* ─── HERO ─────────────────────────────────── */}
        {/*
          LCP FIXES:
          - fetchpriority="high" on <img> (was missing on <picture>)
          - explicit width/height to prevent CLS
          - preload hint when heroSrc is known (avoids loading fallback then switching)
          - Skeleton until category loaded; img fades in on load to prevent flash of fallback.
        */}
        <section className="women-hero" aria-labelledby="women-hero-title">
          {!heroSrc && <div className="women-hero__skeleton" aria-hidden="true" />}
          {heroSrc && (
            <picture aria-hidden="true">
              <ApiImage
                src={heroSrc}
                fallback="/hero.png"
                alt={category?.image?.alt || (pathSubCategory ? `Women's ${slugToDisplayName(pathSubCategory, subcategories)} — ${BRAND_NAME}` : `Shop Women's premium t-shirts — ${BRAND_NAME}`)}
                className={`women-hero__bg ${heroImageLoaded ? 'women-hero__bg--loaded' : ''}`}
                role="presentation"
                loading="eager"
                fetchPriority="high"
                width="1440"
                height="680"
                decoding="async"
                onLoad={() => setHeroImageLoaded(true)}
              />
            </picture>
          )}
          <div className="women-hero__overlay" aria-hidden="true" />
          <div className="women-hero__inner">
            <p className="women-hero__eyebrow" aria-hidden="true">
              <span className="women-hero__eyebrow-dot" />
              New Season · SS 2026
            </p>
            <h1 className="women-hero__title" id="women-hero-title">
              {currentMeta.h1}
            </h1>
            <p className="women-hero__subtitle">{currentMeta.subtitle}</p>
            {isMainPage && subcategories.length > 0 && (
              <div className="women-hero__ctas">
                {subcategories[0] && (
                  <Link
                    to={`${preferredBasePath}/${subcategories[0].slug}`}
                    className="women-hero__cta women-hero__cta--primary"
                  >
                    Shop {subcategories[0].name} <IconArrow />
                  </Link>
                )}
                {subcategories[1] && (
                  <Link
                    to={`${preferredBasePath}/${subcategories[1].slug}`}
                    className="women-hero__cta women-hero__cta--ghost"
                  >
                    Shop {subcategories[1].name}
                  </Link>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ─── TRUST BAR ────────────────────────────── */}
        <div className="women-trust-bar" role="list" aria-label="Shopping benefits">
          {[
            { icon: <IconTruck />, label: 'Free Shipping' },
            { icon: <IconReturn />, label: 'Returns for size & defective only' },
            { icon: <IconLock />, label: 'Secure Checkout' },
            { icon: <IconShield />, label: '100% Authentic' },
          ].map(({ icon, label }) => (
            <div key={label} className="women-trust-bar__item" role="listitem">
              <span className="women-trust-bar__icon" aria-hidden="true">{icon}</span>
              <span className="women-trust-bar__label">{label}</span>
            </div>
          ))}
        </div>

        {/* ─── ERROR STATES ─────────────────────────── */}
        {categoryError && (
          <div className="women-error" role="alert" aria-live="assertive">
            <p className="women-error__message">{categoryError}</p>
            <button
              className="women-error__retry btn-primary"
              onClick={() => {
                setCategoryError(null);
                setSubcategoryError(null);
                window.location.reload();
              }}
            >
              Retry
            </button>
          </div>
        )}

        {subcategoryError && !categoryError && (
          <div className="women-error" role="alert" aria-live="assertive">
            <p className="women-error__message">{subcategoryError}</p>
            <button
              className="women-error__retry btn-primary"
              onClick={() => {
                setSubcategoryError(null);
                window.location.reload();
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* ─── CATEGORY TILES — /women only ─────────── */}
        {isMainPage && (
          <>
            {categoryLoading && !categoryError && (
              <div
                className="women-categories__loading"
                aria-label="Loading categories"
                aria-busy="true"
              >
                <div className="women-categories__skeleton" aria-hidden="true" />
              </div>
            )}

            {!categoryLoading && !categoryError && subcategories.length > 0 && (
              <section className="women-categories" aria-label="Shop by category">
                <p className="women-categories__label" aria-hidden="true">
                  Shop by Category
                </p>
                {/* FIX: H2 inside grid tiles — the label above is decorative */}
                <div className="women-categories__grid">
                  {subcategories.map((sub, index) => (
                    <Link
                      key={sub._id || sub.slug || `sub-${index}`}
                      to={`${preferredBasePath}/${sub.slug}`}
                      className="women-category-tile"
                      aria-label={`Shop ${sub.name}`}
                    >
                      <div className="women-category-tile__img-wrap">
                        <ApiImage
                          src={sub.image
                            ? getImageUrl(sub.image, sub.updatedAt, '/hero.png')
                            : (`/women-${sub.slug}.jpg` || '/hero.png')
                          }
                          fallback="/hero.png"
                          alt={sub.image?.alt || sub.name}
                          className="women-category-tile__img"
                          loading="lazy"
                          width="600"
                          height="450"
                          onLoad={(e) => e.currentTarget.classList.add('women-category-tile__img--loaded')}
                        />
                      </div>
                      <div className="women-category-tile__overlay" aria-hidden="true" />
                      <div className="women-category-tile__content">
                        <span className="women-category-tile__badge" aria-hidden="true">
                          {index === 0 ? 'Bestseller' : 'New In'}
                        </span>
                        {/* FIX: was h2 per tile — correct, but title must be unique */}
                        <h2 className="women-category-tile__title">{sub.name}</h2>
                        <span className="women-category-tile__cta" aria-hidden="true">
                          Shop Now <IconArrow />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {!categoryLoading && !categoryError && subcategories.length === 0 && (
              <div className="women-categories__empty" role="status">
                <p className="women-categories__empty-text">
                  No subcategories available. Please check back later.
                </p>
              </div>
            )}

            {/* ── Launch Social Bar — new brand messaging for 2026 ── */}
            <div
              className="women-social-bar"
              role="complementary"
              aria-label="EVORA launch highlights"
            >
              <div className="women-social-bar__inner">
                <div
                  className="women-social-bar__stars"
                  aria-label="Launching in 2026"
                >
                  <span className="women-social-bar__star-row" aria-hidden="true">
                    {[1, 2, 3, 4, 5].map(i => <IconStar key={i} />)}
                  </span>
                  <span className="women-social-bar__rating" aria-hidden="true">
                    Launching&nbsp;2026
                  </span>
                  <span className="women-social-bar__reviews">
                    · Thoughtfully designed womenswear from India
                  </span>
                </div>
                <span className="women-social-bar__sep" aria-hidden="true" />
                <span className="women-social-bar__tag">
                  <strong>Free shipping</strong> across India · 7‑day size &amp; defect returns
                </span>
              </div>
            </div>
          </>
        )}

        {/* ─── FILTER + SORT BAR ────────────────────── */}
        {!isMainPage && (
          <div
            className="women-filters-bar"
            role="toolbar"
            aria-label="Filter and sort products"
          >
            <div className="women-filters-bar__left">
              <button
                className="women-filters-bar__filter-btn"
                onClick={() => setIsFilterOpen(true)}
                aria-expanded={isFilterOpen}
                aria-controls="filter-drawer"
                aria-haspopup="dialog"
              >
                <IconFilters />
                Filters
                {activeFilterCount > 0 && (
                  <span
                    className="women-filters-bar__badge"
                    aria-label={`${activeFilterCount} filters active`}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            <div className="women-filters-bar__right">
              {/* aria-live so screen readers announce count updates */}
              {!loading && totalProducts > 0 && (
                <span
                  className="women-filters-bar__count"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {totalProducts.toLocaleString('en-IN')} products
                </span>
              )}
              <label className="women-filters-bar__sort-label" htmlFor="women-sort">
                <span className="sr-only">Sort by</span>
              </label>
              <select
                id="women-sort"
                className="women-filters-bar__sort"
                value={sort}
                onChange={handleSortChange}
                aria-label="Sort products"
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ─── PRODUCTS GRID ────────────────────────── */}
        {!isMainPage && (
          <section
            id="women-products-section"
            className="women-products"
            aria-label="Products"
            aria-busy={loading}
          >
            <div className="women-products__container">

              {loading ? (
                <div
                  className="women-products__skeleton"
                  aria-label="Loading products…"
                  aria-hidden="true"
                >
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={`skel-${i}`} className="women-products__skeleton-card" />
                  ))}
                </div>
              ) : productError ? (
                <div className="women-products__error" role="alert" aria-live="assertive">
                  <svg
                    width="40" height="40" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="1.2"
                    className="women-products__error-icon"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8"  x2="12"    y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <p className="women-products__error-title">We couldn't load these products</p>
                  <p className="women-products__error-sub">Check your connection and try again.</p>
                  <button
                    className="women-products__error-retry btn-primary"
                    onClick={() => {
                      setProductError(null);
                      window.location.reload();
                    }}
                  >
                    Try Again
                  </button>
                </div>
              ) : products.length > 0 ? (
                <>
                  <p className="women-products__count" aria-live="polite">
                    <strong>{totalProducts.toLocaleString('en-IN')}</strong> products
                  </p>
                  <div className="women-products__grid">
                    {products.map(product => (
                      <ProductCard
                        key={product._id || product.id}
                        product={product}
                        onAddToCart={name => handleAddToCart(name, product._id || product.id)}
                        onProductClick={() => handleProductClick(product)}
                      />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <nav className="pagination" aria-label="Product pages">
                      {hasPrevPage ? (
                        <Link
                          className="pagination__btn pagination__btn--prev"
                          to={buildListUrl(page - 1)}
                          onClick={(e) => { e.preventDefault(); handlePageChange(page - 1); }}
                          aria-label="Previous page"
                          rel="prev"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path d="M15 18l-6-6 6-6"/>
                          </svg>
                          <span>Previous</span>
                        </Link>
                      ) : (
                        <span className="pagination__btn pagination__btn--prev" aria-disabled="true">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path d="M15 18l-6-6 6-6"/>
                          </svg>
                          <span>Previous</span>
                        </span>
                      )}
                      <div className="pagination__pages" role="list">
                        {buildPageNumbers(page, totalPages).map((item, i) =>
                          item === '...' ? (
                            <span key={`e-${i}`} className="pagination__ellipsis" aria-hidden="true">…</span>
                          ) : item === page ? (
                            <span
                              key={item}
                              className="pagination__page pagination__page--active"
                              aria-current="page"
                              aria-label={`Page ${item}`}
                            >
                              {item}
                            </span>
                          ) : (
                            <Link
                              key={item}
                              className="pagination__page"
                              to={buildListUrl(item)}
                              onClick={(e) => { e.preventDefault(); handlePageChange(item); }}
                              aria-label={`Page ${item}`}
                            >
                              {item}
                            </Link>
                          )
                        )}
                      </div>
                      {hasNextPage ? (
                        <Link
                          className="pagination__btn pagination__btn--next"
                          to={buildListUrl(page + 1)}
                          onClick={(e) => { e.preventDefault(); handlePageChange(page + 1); }}
                          aria-label="Next page"
                          rel="next"
                        >
                          <span>Next</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path d="M9 18l6-6-6-6"/>
                          </svg>
                        </Link>
                      ) : (
                        <span className="pagination__btn pagination__btn--next" aria-disabled="true">
                          <span>Next</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path d="M9 18l6-6-6-6"/>
                          </svg>
                        </span>
                      )}
                    </nav>
                  )}
                </>
              ) : (
                <div className="women-products__empty" role="status" aria-live="polite">
                  <svg
                    width="40" height="40" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="1.2"
                    className="women-products__empty-icon"
                    aria-hidden="true"
                  >
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                    <line x1="8" y1="11" x2="14" y2="11"/>
                  </svg>
                  <p className="women-products__empty-title">
                    {activeFilterCount > 0 ? "No matches for this combination" : "Nothing here yet"}
                  </p>
                  <p className="women-products__empty-sub">
                    {activeFilterCount > 0
                      ? 'Try clearing a filter or browse the full collection below.'
                      : 'New pieces are on the way — check back soon.'}
                  </p>
                  <div className="women-products__empty-actions">
                    {activeFilterCount > 0 && (
                      <button
                        className="women-products__empty-btn btn-ghost"
                        onClick={clearFilters}
                      >
                        Clear Filters
                      </button>
                    )}
                    <Link to={preferredBasePath} className="women-products__empty-btn btn-ghost">
                      Browse All Women's
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ─── RECENTLY VIEWED ──────────────────────── */}
        {recentlyViewed.length > 0 && (
          <section className="recently-viewed" aria-label="Recently viewed products">
            <div className="recently-viewed__container">
              <div className="recently-viewed__header">
                <h2 className="recently-viewed__title">Recently Viewed</h2>
                <div className="recently-viewed__header-actions">
                  <Link to="/recently-viewed" className="recently-viewed__view-all">
                    View all
                  </Link>
                  <button
                    className="recently-viewed__clear"
                    onClick={handleClearRecentlyViewed}
                    aria-label="Clear recently viewed products"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="recently-viewed__scroll" role="list">
                {recentlyViewed.slice(0, 5).map((product, index) => (
                  <Link
                    key={`recent-${product._id ?? product.id}-${index}`}
                    to={`/product/${product.slug || product._id}`}
                    className="recently-viewed__item"
                    role="listitem"
                    onClick={() => handleProductClick(product)}
                  >
                    <div className="recently-viewed__image-wrap">
                      <ApiImage
                        src={product.images?.[0]?.url || product.images?.[0]}
                        fallback="/hero.png"
                        alt={product.name}
                        className="recently-viewed__image"
                        loading="lazy"
                        width="120"
                        height="160"
                      />
                    </div>
                    <p className="recently-viewed__name">{product.name}</p>
                    <p className="recently-viewed__price">
                      {formatCurrency(product.price || 0)}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ─── SEO CONTENT ──────────────────────────── */}
        <section className="women-seo" aria-label="About Shop Women">
          <div className="women-seo__grid">
            <div>
              {/* FIX: was h2 → kept h2 (correct level after hero h1) */}
              <h2 className="women-seo__title">
                Premium Women's T-Shirts Online in India
              </h2>
              <p className="women-seo__text">
                Discover {BRAND_NAME}'s women's premium t-shirts,
                designed for the modern woman. From comfortable round neck t-shirts to
                classic polo shirts, each piece is crafted with attention to detail and
                timeless elegance.
              </p>
              <p className="women-seo__text">
                Explore our range of{' '}
                <Link to={`${preferredBasePath}/round-neck`}>women's round neck t-shirts</Link> and{' '}
                <Link to={`${preferredBasePath}/polo`}>premium polo t-shirts</Link>.{' '}
                Free shipping on all orders. Returns for size &amp; defective only ({RETURNS_DAYS} days).
              </p>
            </div>
            <div>
              {/* FIX: was h3 → correct sub-heading level */}
              <h3 className="women-seo__title women-seo__title--sm">Quick Links</h3>
              <p className="women-seo__text">
                <Link to={`${preferredBasePath}/round-neck`}>Women's Round Neck T-Shirts</Link>
              </p>
              <p className="women-seo__text">
                <Link to={`${preferredBasePath}/polo`}>Women's Polo T-Shirts</Link>
              </p>
              <p className="women-seo__text">
                <Link to="/shop">Shop All (Men &amp; Women)</Link>
              </p>
              <p className="women-seo__text">
                <Link to="/size-guide">Size Guide</Link>
              </p>
              <p className="women-seo__text">
                <Link to="/shipping">Shipping &amp; Returns</Link>
              </p>
            </div>
          </div>
        </section>

      </div>

      {/* ─── FILTER DRAWER — lazy loaded ──────────── */}
      {!isMainPage && (
        <Suspense fallback={null}>
          <FilterDrawer
            id="filter-drawer"
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            subCategories={subcategories.map(sub => sub.slug)}
          />
        </Suspense>
      )}
    </Layout>
  );
}

export default Women;
